import { FormProvider, useForm } from "react-hook-form";
import { RegionSubmission, Region } from "../shared/interfaces/Region";
import { useTranslation } from "react-i18next";
import { FormContainer, FormItem } from "../shared/FormLayout/FormContainer";
import { InputText } from "primereact/inputtext";
import { useContext, useState } from "react";
import { ValidationMessage } from "../shared/ValidationMessage";
import { Dialog } from "primereact/dialog";
import { Col, Container, Row } from "react-bootstrap";
import { Card } from "../shared/Card";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import { ProgressButton } from "../shared/ProgressButton";
import { Stack, Typography } from "@mui/material";
import { CancelButton } from "../shared/CancelButton";
import { AuthContext } from "../contexts/AuthContext";
import { useCompanyCreation } from "../data/Settings/useCompanyCreation";
import { RegionType } from "../shared/enums/Region";
import { useAlternativeRegionNames } from "../data/Settings/useAlternativeRegionNames";

interface Props {
  open?: boolean;
  onClose: () => void;
  onCompanyCreated: (companyId: number, companyName: string) => void;
  customerId: number;
  existingCompanies: Region[] | undefined;
  refreshCompanyData: () => void;
}

export function CompanyCreate(props: Props) {
  const { t } = useTranslation();
  const formValidation = useForm({ mode: "onBlur" });

  const { onCompanyCreated, customerId } = props;

  const authContext = useContext(AuthContext);
  const userCanAddCompany = authContext.canI("add", "companies");

  const altRegionNames = useAlternativeRegionNames(customerId);

  const [showErrorToast, setShowErrorToast] = useState(false);

  const [newCompany, setNewCompany] = useState<Partial<Region>>({});

  const onSubmit = formValidation.handleSubmit(() => {
    props.refreshCompanyData();
    if (userCanAddCompany) {
      setShowErrorToast(false);

      const region = {
        ...newCompany,
        customerId: customerId,
        regionType: RegionType.Company,
      };

      const companySubmission: RegionSubmission = {
        regionName: newCompany.name,
        region: region,
      };

      companyCreate.mutate(companySubmission);
    }
  });

  const companyCreate = useCompanyCreation({
    onSuccess: (companyId) => onCompanyCreated(companyId, newCompany.name || ""),
    onError: () => setShowErrorToast(true),
  });

  function handleHide() {
    setNewCompany({});
    props.onClose();
  }

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const fieldName = event.target.name as keyof RegionSubmission;
    const value = event.target.value;
    formValidation.clearErrors(fieldName);
    formValidation.setValue(fieldName, value, { shouldValidate: false });
    setNewCompany({ ...newCompany, [fieldName]: value });
  };

  const companyDetailsList: FormItem[] = [
    {
      label: t("settings.companyName", {
        val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
      }),
      required: true,
      value: (
        <div>
          <div>
            <InputText
              value={newCompany.name?.toString() || ""}
              maxLength={200}
              className={formValidation.formState.errors.name ? "p-invalid" : undefined}
              id={"name"}
              {...formValidation.register("name", {
                value: newCompany.name,
                validate: {
                  notEmpty: (value) => value && value !== "",
                  notDuplicate: (value) => !props.existingCompanies?.some((company) => company.name === value),
                },
              })}
              onChange={handleInputChange}
              name={"name"}
            />
          </div>
          <div>
            {formValidation.formState.errors?.name && formValidation.formState.errors.name.type === "notEmpty" && (
              <ValidationMessage
                invalidMessage={t("settings.emptyCompanyName", {
                  val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
                })}
                validationType="error"
                showIcon
              />
            )}
            {formValidation.formState.errors?.name && formValidation.formState.errors.name.type === "notDuplicate" && (
              <ValidationMessage
                invalidMessage={t("settings.companyNameDuplication", {
                  val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
                })}
                validationType="error"
                showIcon
              />
            )}
          </div>
        </div>
      ),
    },
  ];

  const header = (
    <Typography
      variant="h5"
      component="p"
    >{`${altRegionNames.data?.companyAltName ?? t("common.company")} `}</Typography>
  );

  const footer = (
    <Stack direction="row" justifyContent="flex-end" spacing={1} my={0.5}>
      <ProgressButton onClick={onSubmit} disabled={companyCreate.isLoading} loading={companyCreate.isLoading}>
        {" "}
        {t("common.create")}{" "}
      </ProgressButton>
      <CancelButton onClick={handleHide} disabled={companyCreate.isLoading} className="m1-1" />
    </Stack>
  );

  return (
    <FormProvider {...formValidation}>
      <Dialog
        header={header}
        footer={footer}
        visible={props.open}
        onHide={handleHide}
        breakpoints={{ "1960px": "75vw", "1300px": "90vw" }}
        style={{ width: "60vw" }}
      >
        <Container fluid>
          <Row>
            <Col xl={6} className="px-0">
              <Card
                title={t("settings.companyDetails", {
                  val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
                })}
                className="mb-3 "
              >
                <FormContainer dataList={companyDetailsList} />
              </Card>
              <div>
                {showErrorToast && (
                  <MessageToast
                    text={t("common.dataCreateError", {
                      val: altRegionNames.data?.companyAltName ?? "$t(common.company)",
                    })}
                    mode={ToastMode.Error}
                    show={showErrorToast}
                    autoHide
                  ></MessageToast>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </Dialog>
    </FormProvider>
  );
}
